import { Experiment } from '@naturalcycles/shared'
import { SupportedLocale } from '@src/cnst/translations.cnst'
import {
  FsaBadge,
  FsaBadgeAlignment,
  FsaBadgeTheme,
} from '@src/components/fsa-badge/FsaBadge.component'
import { NcLink } from '@src/components/nc-link/NcLink.component'
import { languageContext } from '@src/context/language.context'
import { withSupportedUrl } from '@src/helpers/locale'
import { useAppSelector } from '@src/hooks/useAppSelector'
import { useExperiment } from '@src/hooks/useExperiment'
import { GeolocationCountry, selectGeolocation } from '@src/store/geolocation/geolocation.slice'
import classNames from 'classnames'
import { ReactNode, useContext, useMemo } from 'react'
import { ClientOnly } from '../client-only/ClientOnly.component'
import styles from './HeroBadges.module.scss'

export interface HeroBadgesProps {
  ratings: {
    rating: string
    storeName: string
    context: string
  }
  bannerBadges: BannerBadge[]
}

export function HeroBadges({ ratings, bannerBadges }: HeroBadgesProps): ReactNode {
  const { locale } = useContext(languageContext)
  const { country } = useAppSelector(selectGeolocation)

  const isEnglish = locale === SupportedLocale.enUS
  const isUSA = country === GeolocationCountry.US

  const [AB400] = useExperiment(Experiment.NCWEB_ROW_BADGES)
  const ab400Status = {
    testOne: AB400 === 'test',
    testTwo: AB400 === 'test2',
    testThree: AB400 === 'test3',
    applied: ['test', 'test2', 'test3'].includes(AB400 || ''),
  }

  const badge = useMemo(() => {
    // Show the FDA Cleared badge for all users in the USA.
    // Users on the English page but located outside the USA will see an alternative badge.
    // This behavior is also written in the Dato
    if (!isEnglish || isUSA) return bannerBadges[0]!
    return bannerBadges[1]!
  }, [isEnglish, isUSA, bannerBadges])

  const starIconUrl = '/icons/star-rounded.svg'

  const madeIn = {
    [SupportedLocale.svSE]: 'Tillverkad i',
    [SupportedLocale.ptBR]: 'Feito na',
    [SupportedLocale.esUS]: 'Hecho en',
    [SupportedLocale.enUS]: 'Made in',
    [SupportedLocale.enCA]: 'Made in',
    [SupportedLocale.enGB]: 'Made in',
  }

  const sweden = {
    [SupportedLocale.svSE]: '<b>Sverige</b>',
    [SupportedLocale.ptBR]: '<b>Suécia</b>',
    [SupportedLocale.esUS]: '<b>Suecia</b>',
    [SupportedLocale.enUS]: '<b>Sweden</b>',
    [SupportedLocale.enCA]: '<b>Sweden</b>',
    [SupportedLocale.enGB]: '<b>Sweden</b>',
  }

  const regulated = {
    [SupportedLocale.svSE]: '<b>Reglerad</b> </br> medicinteknisk produkt',
    [SupportedLocale.ptBR]: 'Dispositivo médico</br> <b>regulamentado</b>',
    [SupportedLocale.esUS]: 'Dispositivo médico</br> <b>regulado</b>',
    [SupportedLocale.enUS]: '<b>Regulated</b> </br> medical device',
    [SupportedLocale.enCA]: '<b>Regulated</b> </br> medical device',
    [SupportedLocale.enGB]: '<b>Regulated</b> </br> medical device',
  }

  const regulatedBadge = (
    <div className={styles.HeroBadges__regulated}>
      <img src="icons/ab400/regulated_badge.svg" alt="" />
      <p
        dangerouslySetInnerHTML={{ __html: regulated[locale] }}
        className={styles.HeroBadges__regulatedText}
      />
    </div>
  )

  const usersBadge = (
    <div
      className={classNames(styles.HeroBadges__container, {
        [styles.HeroBadges__container___US]: isUSA,
      })}
    >
      <span className={styles.HeroBadges__highlightNumber}>{badge.optionalField}</span>
      <span className={styles.HeroBadges__highlightText}>{badge.title}</span>
      <p className={styles.HeroBadges__text}>{badge.description}</p>
    </div>
  )

  const swedenBadge = (
    <div
      className={classNames(styles.HeroBadges__sweden, {
        [styles.HeroBadges__sweden___test2]: ab400Status.testTwo,
      })}
    >
      <img src="icons/ab400/swedish_flag.svg" alt="" />
      <div className={styles.HeroBadges__swedenContent}>
        <p
          className={styles.HeroBadges__swedenText}
          dangerouslySetInnerHTML={{ __html: madeIn[locale] }}
        />
        <p
          className={styles.HeroBadges__swedenText}
          dangerouslySetInnerHTML={{ __html: sweden[locale] }}
        />
      </div>
    </div>
  )

  function renderSecondBadge(): ReactNode {
    const { applied, testOne, testTwo, testThree } = ab400Status

    if (isUSA || !applied || testOne) {
      return usersBadge
    }

    const isAB400VariantTwoOrThree = testTwo || testThree
    if (!isUSA && isAB400VariantTwoOrThree) {
      return regulatedBadge
    }
  }

  function renderThirdBadge(): ReactNode {
    const { applied, testOne, testTwo, testThree } = ab400Status

    if (isUSA || !applied) {
      return (
        <FsaBadge
          hasBlueTick
          theme={FsaBadgeTheme.Large}
          isReversedForTranslations={false}
          alignment={FsaBadgeAlignment.Centered}
        />
      )
    }

    if (!isUSA && (testOne || testTwo)) {
      return swedenBadge
    }

    if (!isUSA && testThree) {
      return usersBadge
    }
  }

  return (
    <div
      className={classNames(styles.HeroBadges, {
        [styles.HeroBadges___test2]: ab400Status.testTwo,
      })}
    >
      <NcLink className={styles.HeroBadge__appStore} to={withSupportedUrl('reviews', locale)}>
        <div className={styles.HeroBadges__badge}>
          <div className={styles.HeroBadges__highlight}>
            {ratings.rating}
            <img className={styles.HeroBadges__icon} src={starIconUrl} alt="Star Icon" />
            <span className={styles.HeroBadges__storeName}>{ratings.storeName}</span>
          </div>
          <p className={styles.HeroBadges__text}>{ratings.context}</p>
        </div>
      </NcLink>

      <ClientOnly>
        <div className={styles.HeroBadges__badge}>{renderSecondBadge()}</div>

        <div className={styles.HeroBadges__badge}>
          <div className={styles.HeroBadges__container}>{renderThirdBadge()}</div>
        </div>
      </ClientOnly>
    </div>
  )
}

export interface BannerBadge {
  optionalField: string
  title: string
  description: string
}
